import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from 'axios';
import Layout from '../components/Layout';
import { X, Plus, Calendar, User, Tag, Paperclip, MessageSquare, Clock, Users, Upload, Trash2, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800', error: '#f44336' };

function KanbanBoard({ user, onLogout }) {
  const [boards, setBoards] = useState([]);
  const [currentBoard, setCurrentBoard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewBoard, setShowNewBoard] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [newTaskColumn, setNewTaskColumn] = useState('todo');
  const [newTaskData, setNewTaskData] = useState({ 
    title: '', 
    description: '', 
    priority: 'medium', 
    assigned_to: [],  // Multi-user
    due_date: '',
    labels: []
  });
  const [newBoardName, setNewBoardName] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [newComment, setNewComment] = useState('');
  const [showNewLabel, setShowNewLabel] = useState(false);
  const [newLabel, setNewLabel] = useState({ name: '', color: '#4caf50' });

  const columns = [
    { id: 'todo', name: 'To Do', color: '#ff9800' },
    { id: 'in_progress', name: 'In Progress', color: '#2196f3' },
    { id: 'review', name: 'Review', color: '#9c27b0' },
    { id: 'done', name: 'Done', color: '#4caf50' }
  ];

  const priorityColors = {
    low: '#8bc34a',
    medium: '#ff9800',
    high: '#f44336'
  };

  useEffect(() => {
    fetchBoards();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (currentBoard) {
      fetchTasks();
      fetchStats();
      fetchLabels();
    }
  }, [currentBoard, filterPriority]);

  const fetchBoards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/boards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(response.data || []);
      if (response.data && response.data.length > 0 && !currentBoard) {
        setCurrentBoard(response.data[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching boards:', error);
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      // Always fetch only user's assigned tasks - no option to see all
      let url = `${API}/api/boards/${currentBoard.id}/tasks?my_tasks=true`;
      if (filterPriority) url += `&priority=${filterPriority}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get stats for user's tasks only
      const response = await axios.get(`${API}/api/boards/${currentBoard.id}/stats?my_tasks=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLabels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/boards/${currentBoard.id}/labels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLabels(response.data || []);
    } catch (error) {
      console.error('Error fetching labels:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees/for-dropdowns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTaskDetail = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskDetail(response.data);
    } catch (error) {
      console.error('Error fetching task detail:', error);
    }
  };

  const handleCreateTask = async () => {
    if (!newTaskData.title) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/boards/${currentBoard.id}/tasks`, {
        ...newTaskData,
        status: newTaskColumn
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowNewTask(false);
      setNewTaskData({ title: '', description: '', priority: 'medium', assigned_to: [], due_date: '', labels: [] });
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleCreateBoard = async () => {
    if (!newBoardName) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/boards`, {
        name: newBoardName,
        description: ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowNewBoard(false);
      setNewBoardName('');
      fetchBoards();
      setCurrentBoard(response.data.board);
    } catch (error) {
      console.error('Error creating board:', error);
    }
  };

  const handleCreateLabel = async () => {
    if (!newLabel.name) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/boards/${currentBoard.id}/labels`, newLabel, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowNewLabel(false);
      setNewLabel({ name: '', color: '#4caf50' });
      fetchLabels();
    } catch (error) {
      console.error('Error creating label:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment || !taskDetail) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/tasks/${taskDetail.id}/comments`, {
        comment: newComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewComment('');
      fetchTaskDetail(taskDetail.id);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !taskDetail) return;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/api/tasks/${taskDetail.id}/attachments`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      fetchTaskDetail(taskDetail.id);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!taskDetail) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/tasks/${taskDetail.id}/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTaskDetail(taskDetail.id);
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  const handleUpdateTask = async (updates) => {
    if (!taskDetail) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/tasks/${taskDetail.id}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTaskDetail(taskDetail.id);
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const { draggableId, destination } = result;
    const newStatus = destination.droppableId;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/tasks/${draggableId}/move?new_status=${newStatus}&new_order=${destination.index}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error moving task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTaskDetail(null);
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const toggleAssignee = (empId) => {
    setNewTaskData(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(empId)
        ? prev.assigned_to.filter(id => id !== empId)
        : [...prev.assigned_to, empId]
    }));
  };

  const toggleLabel = (labelId) => {
    setNewTaskData(prev => ({
      ...prev,
      labels: prev.labels.includes(labelId)
        ? prev.labels.filter(id => id !== labelId)
        : [...prev.labels, labelId]
    }));
  };

  const getTasksByColumn = (columnId) => {
    return tasks.filter(task => task.status === columnId);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{borderColor: COLORS.primary}}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <select
              value={currentBoard?.id || ''}
              onChange={(e) => setCurrentBoard(boards.find(b => b.id === e.target.value))}
              className="px-4 py-2 border rounded-lg text-lg font-bold"
              style={{color: COLORS.primary}}
            >
              {boards.map(board => (
                <option key={board.id} value={board.id}>{board.name}</option>
              ))}
            </select>
            <button
              onClick={() => setShowNewBoard(true)}
              className="px-4 py-2 text-white rounded-lg flex items-center gap-2"
              style={{backgroundColor: COLORS.primary}}
            >
              <Plus className="w-4 h-4" /> New Board
            </button>
          </div>
          
          {/* Stats */}
          <div className="flex gap-4">
            <div className="bg-white rounded-lg px-4 py-2 shadow">
              <span className="text-gray-600 text-sm">Total Tasks</span>
              <p className="text-2xl font-bold" style={{color: COLORS.primary}}>{stats.total || 0}</p>
            </div>
            {columns.map(col => (
              <div key={col.id} className="bg-white rounded-lg px-4 py-2 shadow">
                <span className="text-gray-600 text-sm">{col.name}</span>
                <p className="text-2xl font-bold" style={{color: col.color}}>{stats[col.id] || 0}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6 items-center flex-wrap">
          <div className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium flex items-center gap-2">
            <User className="w-4 h-4" />
            My Tasks Only
          </div>
          
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <button
            onClick={() => { setNewTaskColumn('todo'); setShowNewTask(true); }}
            className="px-4 py-2 text-white rounded-lg flex items-center gap-2 ml-auto"
            style={{backgroundColor: COLORS.success}}
          >
            <Plus className="w-4 h-4" /> New Task
          </button>
        </div>

        {/* Kanban Board */}
        {!currentBoard ? (
          <div className="text-center py-12 bg-white rounded-lg">
            <h3 className="text-xl text-gray-600 mb-4">No boards yet!</h3>
            <button
              onClick={() => setShowNewBoard(true)}
              className="px-6 py-3 text-white rounded-lg"
              style={{backgroundColor: COLORS.primary}}
            >
              Create Your First Board
            </button>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="grid grid-cols-4 gap-4">
              {columns.map(column => (
                <div key={column.id} className="bg-gray-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{backgroundColor: column.color}}></span>
                      {column.name}
                      <span className="text-gray-500 text-sm">({getTasksByColumn(column.id).length})</span>
                    </h3>
                    <button
                      onClick={() => { setNewTaskColumn(column.id); setShowNewTask(true); }}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <Droppable droppableId={column.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`min-h-[200px] space-y-3 ${snapshot.isDraggingOver ? 'bg-blue-50' : ''}`}
                      >
                        {getTasksByColumn(column.id).map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                onClick={() => { setSelectedTask(task); fetchTaskDetail(task.id); }}
                                className={`bg-white rounded-lg p-3 shadow cursor-pointer hover:shadow-md transition-shadow ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                              >
                                {/* Labels */}
                                {task.labels && task.labels.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {task.labels.map(label => (
                                      <span
                                        key={label.id}
                                        className="px-2 py-0.5 rounded text-xs text-white"
                                        style={{backgroundColor: label.color}}
                                      >
                                        {label.name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                
                                <h4 className="font-medium mb-2">{task.title}</h4>
                                
                                {task.description && (
                                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{task.description}</p>
                                )}
                                
                                {/* Assignees (Multi-user) */}
                                {task.assigned_to_names && task.assigned_to_names.length > 0 && (
                                  <div className="flex items-center gap-1 mb-2">
                                    <Users className="w-3 h-3 text-gray-400" />
                                    <div className="flex -space-x-2">
                                      {task.assigned_to_names.slice(0, 3).map((assignee, idx) => (
                                        <div
                                          key={idx}
                                          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white"
                                          style={{backgroundColor: COLORS.primary}}
                                          title={assignee.name}
                                        >
                                          {assignee.name?.charAt(0) || '?'}
                                        </div>
                                      ))}
                                      {task.assigned_to_names.length > 3 && (
                                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white bg-gray-400">
                                          +{task.assigned_to_names.length - 3}
                                        </div>
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-500 ml-1">
                                      {task.assigned_to_names.length === 1 
                                        ? task.assigned_to_names[0].name 
                                        : `${task.assigned_to_names.length} assignees`}
                                    </span>
                                  </div>
                                )}
                                
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                  <div className="flex items-center gap-2">
                                    {task.due_date && (
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {new Date(task.due_date).toLocaleDateString()}
                                      </span>
                                    )}
                                    {task.attachments?.length > 0 && (
                                      <span className="flex items-center gap-1">
                                        <Paperclip className="w-3 h-3" />
                                        {task.attachments.length}
                                      </span>
                                    )}
                                  </div>
                                  <span
                                    className="w-2 h-2 rounded-full"
                                    style={{backgroundColor: priorityColors[task.priority]}}
                                    title={task.priority}
                                  ></span>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        )}

        {/* New Task Modal */}
        {showNewTask && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold" style={{color: COLORS.primary}}>Create New Task</h2>
                <button onClick={() => setShowNewTask(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input
                    type="text"
                    value={newTaskData.title}
                    onChange={(e) => setNewTaskData({...newTaskData, title: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Task title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={newTaskData.description}
                    onChange={(e) => setNewTaskData({...newTaskData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={3}
                    placeholder="Task description"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      value={newTaskData.priority}
                      onChange={(e) => setNewTaskData({...newTaskData, priority: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Due Date</label>
                    <input
                      type="date"
                      value={newTaskData.due_date}
                      onChange={(e) => setNewTaskData({...newTaskData, due_date: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
                
                {/* Multi-user Assignment */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <Users className="w-4 h-4 inline mr-1" />
                    Assign To (Select multiple)
                  </label>
                  <div className="border rounded-lg p-2 max-h-40 overflow-y-auto">
                    {employees.map(emp => (
                      <label key={emp.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newTaskData.assigned_to.includes(emp.id)}
                          onChange={() => toggleAssignee(emp.id)}
                          className="rounded"
                        />
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{backgroundColor: COLORS.primary}}>
                          {emp.name?.charAt(0) || '?'}
                        </div>
                        <span>{emp.name}</span>
                      </label>
                    ))}
                  </div>
                  {newTaskData.assigned_to.length > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      {newTaskData.assigned_to.length} employee(s) selected
                    </p>
                  )}
                </div>
                
                {/* Labels */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <Tag className="w-4 h-4 inline mr-1" />
                    Labels
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {labels.map(label => (
                      <button
                        key={label.id}
                        onClick={() => toggleLabel(label.id)}
                        className={`px-3 py-1 rounded-full text-sm ${newTaskData.labels.includes(label.id) ? 'ring-2 ring-offset-1' : ''}`}
                        style={{
                          backgroundColor: label.color,
                          color: 'white',
                          ringColor: label.color
                        }}
                      >
                        {label.name}
                      </button>
                    ))}
                    <button
                      onClick={() => setShowNewLabel(true)}
                      className="px-3 py-1 rounded-full text-sm border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50"
                    >
                      + Add Label
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    onClick={() => setShowNewTask(false)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateTask}
                    className="px-4 py-2 text-white rounded-lg"
                    style={{backgroundColor: COLORS.primary}}
                  >
                    Create Task
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Detail Modal */}
        {taskDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-xl font-bold" style={{color: COLORS.primary}}>{taskDetail.title}</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDeleteTask(taskDetail.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <button onClick={() => setTaskDetail(null)} className="p-2 hover:bg-gray-100 rounded">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-3 gap-6">
                  {/* Main Content */}
                  <div className="col-span-2 space-y-6">
                    {/* Description */}
                    <div>
                      <h3 className="font-medium mb-2">Description</h3>
                      <p className="text-gray-600">{taskDetail.description || 'No description'}</p>
                    </div>
                    
                    {/* Labels */}
                    <div>
                      <h3 className="font-medium mb-2 flex items-center gap-2">
                        <Tag className="w-4 h-4" /> Labels
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {taskDetail.labels?.map(label => (
                          <span
                            key={label.id}
                            className="px-3 py-1 rounded-full text-sm text-white"
                            style={{backgroundColor: label.color}}
                          >
                            {label.name}
                          </span>
                        ))}
                        {(!taskDetail.labels || taskDetail.labels.length === 0) && (
                          <span className="text-gray-400 text-sm">No labels</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Attachments */}
                    <div>
                      <h3 className="font-medium mb-2 flex items-center gap-2">
                        <Paperclip className="w-4 h-4" /> Attachments
                      </h3>
                      <div className="space-y-2">
                        {taskDetail.attachments?.map(att => (
                          <div key={att.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm">{att.filename}</span>
                            <div className="flex items-center gap-2">
                              <a
                                href={`${API}${att.file_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 text-sm hover:underline"
                              >
                                Download
                              </a>
                              <button
                                onClick={() => handleDeleteAttachment(att.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        <label className="flex items-center gap-2 p-2 border border-dashed rounded cursor-pointer hover:bg-gray-50">
                          <Upload className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-500">Upload attachment</span>
                          <input type="file" className="hidden" onChange={handleFileUpload} />
                        </label>
                      </div>
                    </div>
                    
                    {/* Comments */}
                    <div>
                      <h3 className="font-medium mb-2 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Comments
                      </h3>
                      <div className="space-y-3 mb-4">
                        {taskDetail.comments?.map(comment => (
                          <div key={comment.id} className="bg-gray-50 rounded p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{backgroundColor: COLORS.primary}}>
                                {comment.user_name?.charAt(0) || '?'}
                              </div>
                              <span className="font-medium text-sm">{comment.user_name}</span>
                              <span className="text-xs text-gray-400">
                                {new Date(comment.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 ml-8">{comment.comment}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Add a comment..."
                          className="flex-1 px-3 py-2 border rounded-lg text-sm"
                          onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                        />
                        <button
                          onClick={handleAddComment}
                          className="px-4 py-2 text-white rounded-lg text-sm"
                          style={{backgroundColor: COLORS.primary}}
                        >
                          Send
                        </button>
                      </div>
                    </div>
                    
                    {/* Activity Log */}
                    <div>
                      <h3 className="font-medium mb-2 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Activity Log
                      </h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {taskDetail.activities?.map(activity => (
                          <div key={activity.id} className="flex items-start gap-2 text-sm">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5"></div>
                            <div>
                              <span className="font-medium">{activity.user_name}</span>
                              <span className="text-gray-600"> {activity.details}</span>
                              <p className="text-xs text-gray-400">
                                {new Date(activity.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                        {(!taskDetail.activities || taskDetail.activities.length === 0) && (
                          <p className="text-gray-400 text-sm">No activity yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Sidebar */}
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-500">Status</label>
                      <select
                        value={taskDetail.status}
                        onChange={(e) => handleUpdateTask({ status: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg mt-1"
                      >
                        {columns.map(col => (
                          <option key={col.id} value={col.id}>{col.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="text-sm text-gray-500">Priority</label>
                      <select
                        value={taskDetail.priority}
                        onChange={(e) => handleUpdateTask({ priority: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg mt-1"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="text-sm text-gray-500">Due Date</label>
                      <input
                        type="date"
                        value={taskDetail.due_date || ''}
                        onChange={(e) => handleUpdateTask({ due_date: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg mt-1"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-gray-500">Assignees</label>
                      <div className="mt-1 space-y-2">
                        {taskDetail.assigned_to_names?.map(assignee => (
                          <div key={assignee.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{backgroundColor: COLORS.primary}}>
                              {assignee.name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="text-sm font-medium">{assignee.name}</p>
                              <p className="text-xs text-gray-400">{assignee.email}</p>
                            </div>
                          </div>
                        ))}
                        {(!taskDetail.assigned_to_names || taskDetail.assigned_to_names.length === 0) && (
                          <p className="text-gray-400 text-sm">No assignees</p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm text-gray-500">Created By</label>
                      <p className="font-medium">{taskDetail.created_by_name}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(taskDetail.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New Board Modal */}
        {showNewBoard && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4" style={{color: COLORS.primary}}>Create New Board</h2>
              <input
                type="text"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg mb-4"
                placeholder="Board name"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowNewBoard(false)} className="px-4 py-2 border rounded-lg">
                  Cancel
                </button>
                <button
                  onClick={handleCreateBoard}
                  className="px-4 py-2 text-white rounded-lg"
                  style={{backgroundColor: COLORS.primary}}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Label Modal */}
        {showNewLabel && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4" style={{color: COLORS.primary}}>Create New Label</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Label Name</label>
                  <input
                    type="text"
                    value={newLabel.name}
                    onChange={(e) => setNewLabel({...newLabel, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Bug, Feature, Urgent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Color</label>
                  <div className="flex gap-2 flex-wrap">
                    {['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#ff9800', '#ff5722'].map(color => (
                      <button
                        key={color}
                        onClick={() => setNewLabel({...newLabel, color})}
                        className={`w-8 h-8 rounded-full ${newLabel.color === color ? 'ring-2 ring-offset-2' : ''}`}
                        style={{backgroundColor: color, ringColor: color}}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm">Preview:</span>
                  <span
                    className="px-3 py-1 rounded-full text-sm text-white"
                    style={{backgroundColor: newLabel.color}}
                  >
                    {newLabel.name || 'Label'}
                  </span>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowNewLabel(false)} className="px-4 py-2 border rounded-lg">
                  Cancel
                </button>
                <button
                  onClick={handleCreateLabel}
                  className="px-4 py-2 text-white rounded-lg"
                  style={{backgroundColor: COLORS.primary}}
                >
                  Create Label
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default KanbanBoard;
